import { cloneDeep, set } from 'lodash';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import { ComponentListModal, PreviewModal } from 'src/components/modal';
import { EditOOComponents } from 'src/features/locations/overrideLocationPage/modules/editComponents/EditOOComponents';
import { IComponent } from 'src/interfaces';

import {
  IDefaultSection,
  IGridSection,
  IScheduledSectionComponent,
} from '../../ScheduleComponentPage.interface';
import { ComponentAccordion } from '../componentAccordion';
import { defaultDrawerProps } from '../screensDrawer';
import { useSectionDetailDrawer } from './hooks/SectionDetailDrawer.hook';
import { SectionWrapper } from './SectionDetailDrawer.styled';

export interface ISectionDetailDrawer {
  activeEditableComponent?: IScheduledSectionComponent | null;
  setActiveEditableComponent?: React.Dispatch<
    React.SetStateAction<IScheduledSectionComponent | null>
  >;
  showSectionDetailDrawer: boolean;
  selectedSection: IGridSection | IDefaultSection;
  isCodConfirmationOpen: boolean | undefined;
  duration?: number;
  options?: {
    showAdditionalComponentButton?: boolean;
    showComponentSubTitle?: boolean;
    enableBackup?: boolean;
    isOverriding?: boolean;
  };
  onCloseSectionDetailDrawer: () => void;
  onUpdateScreensGridSectionsWhenSectionChanges: (
    section: IGridSection | IDefaultSection,
  ) => void;
  onSwapComponent: (selectComponent: IScheduledSectionComponent) => void;
  onAddAdditionalComponent: () => void;
  handleSectionComponentChange?: (
    updatedSectionComponent: IScheduledSectionComponent,
  ) => void;
  handleSectionComponentRemove?: (
    updatedSectionComponent: IScheduledSectionComponent,
  ) => void;
}

export const SectionDetailDrawer = (props: ISectionDetailDrawer) => {
  const {
    showSectionDetailDrawer,
    selectedSection,
    isCodConfirmationOpen,
    options,
    onSwapComponent,
    onAddAdditionalComponent,
    activeEditableComponent,
    setActiveEditableComponent,
  } = props;

  const {
    openBackupComponentListModal,
    backupComponentPreviewModal,
    filterParameters,
    selectedPreviewComponent,
    handleCloseSectionDetailDrawer,
    onDeletedAssemblyComponent,
    onAddNewBackup,
    onCloseBackupComponentListModal,
    onSelectBackup,
    onDeleteBackup,
    onReorderBackup,
    onPreviewBackup,
    handleUpdateComponent,
  } = useSectionDetailDrawer(props);

  const onOverrideConfig = (field: string | string[], value: any | any[]) => {
    if (!activeEditableComponent) return;

    setActiveEditableComponent?.((prev) => {
      if (!prev || !prev.component) return prev;
      const newConfig = cloneDeep(prev.component.config || {});

      set(newConfig, field, value);

      if (!newConfig.productList) {
        newConfig.productList = { config: {}, groups: {} };
      }

      const groups = newConfig?.productList?.groups ?? {};
      const scheduleItemOverrideVariations = Object.values(groups).flatMap(
        (group: any) =>
          Object.values(group.products || {}).map((p: any) => ({
            product_variation_product_code: p.productCode,
          })),
      );

      const updatedComponentWithNewConfig = {
        ...prev,
        component: { ...prev.component, config: newConfig },
        isOverridden: true,

        //reset start and end dates for override.
        scheduleStartDate: null,
        scheduleEndDate: null,
        override: {
          ...(prev.override || {}),
          schedule_item_override_variations: scheduleItemOverrideVariations,
        },
      } as IScheduledSectionComponent;

      handleUpdateComponent(updatedComponentWithNewConfig);

      return updatedComponentWithNewConfig;
    });
  };

  return (
    <Drawer open={showSectionDetailDrawer} {...defaultDrawerProps}>
      {showSectionDetailDrawer && (
        <>
          <SidebarHeader
            label={selectedSection.name}
            onBackClick={() => {
              if (activeEditableComponent && setActiveEditableComponent) {
                setActiveEditableComponent(null);
              } else {
                handleCloseSectionDetailDrawer();
              }
            }}
          />

          <SectionWrapper item xs={12}>
            {activeEditableComponent ? (
              <EditOOComponents
                key={activeEditableComponent.uid}
                assemblyComponent={activeEditableComponent}
                onOverrideConfig={onOverrideConfig}
              />
            ) : (
              <ComponentAccordion
                components={selectedSection.components ?? []}
                isCodConfirmationOpen={isCodConfirmationOpen}
                options={options}
                onRemoveComponent={onDeletedAssemblyComponent}
                onSwapComponent={onSwapComponent}
                onAddAdditionalComponent={onAddAdditionalComponent}
                onAddNewBackup={onAddNewBackup}
                onDeleteBackup={onDeleteBackup}
                onReorderBackup={onReorderBackup}
                onPreviewBackup={onPreviewBackup}
                handleUpdateComponent={handleUpdateComponent}
                onClickEditProduct={(component) => {
                  setActiveEditableComponent?.(component);
                }}
              />
            )}
          </SectionWrapper>

          {openBackupComponentListModal && (
            <ComponentListModal
              open={openBackupComponentListModal}
              filters={filterParameters}
              onClose={onCloseBackupComponentListModal}
              onSelectedComponent={onSelectBackup}
            />
          )}

          {backupComponentPreviewModal.open && (
            <PreviewModal
              open={backupComponentPreviewModal.open}
              onClose={backupComponentPreviewModal.onCloseModal}
              type="component"
              content={selectedPreviewComponent?.component as IComponent}
              hideButtons
            />
          )}
        </>
      )}
    </Drawer>
  );
};
